import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const pages = [
    { id: "announcement", label: "Announcement", icon: "📢" },
    // { id: "dashboard", label: "Dashboard", icon: "⊞" },
    // { id: "reports", label: "Reports", icon: "📊" },
    // { id: "finance", label: "Finance", icon: "💰" },
    // { id: "hr", label: "HR Panel", icon: "👥" },
    // { id: "inventory", label: "Inventory", icon: "📦" },
    // { id: "settings", label: "Settings", icon: "⚙️" },
    // { id: "analytics", label: "Analytics", icon: "📈" },
    // { id: "support", label: "Support", icon: "🎧" },
    { id: "asset_management", label: "Asset Management", icon: "🧰" },
    // { id: "asset_approvals", label: "Asset Approvals", icon: "✅" },
    // { id: "my_assets", label: "My Assets", icon: "💼" },
    // { id: "vendor_master", label: "Vendor Master", icon: "🏢" },
    // { id: "asset_reports", label: "Asset Reports", icon: "📋" }
];

const getInitials = (firstName = "", lastName = "") =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

function EmpAvatar({ emp, style, fontSize = 12 }) {
    const [imgError, setImgError] = useState(false);
    const showPhoto = emp.photoPath && !imgError;

    const containerStyle = {
        width: style.width,
        height: style.height,
        borderRadius: "50%",
        flexShrink: 0,
        overflow: "hidden",
        ...(showPhoto
            ? {}
            : {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: style.background,
                color: style.color,
                fontSize,
                fontWeight: 700,
            }),
    };

    return (
        <div style={containerStyle}>
            {showPhoto ? (
                <img
                    src={`${API_BASE_URL}/uploads/${emp.photoPath}`}
                    alt={`${emp.firstName} ${emp.lastName}`}
                    onError={() => setImgError(true)}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center top",
                        display: "block",
                    }}
                />
            ) : (
                getInitials(emp.firstName, emp.lastName)
            )}
        </div>
    );
}

const NAVBAR_HEIGHT = 64;

export default function AdminPermissions() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState({});
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false); // ← NEW: prevents double-save
    const [search, setSearch] = useState("");

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("token");

                // ← UPDATED: fetch employees + existing permissions in parallel
                const [empRes, permRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/employees/active/summary`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    axios.get(`${API_BASE_URL}/api/permissions`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);

                const data = empRes.data;
                setEmployees(data);

                // ← UPDATED: seed from backend data, fall back to false for missing pages
                const init = {};
                data.forEach((emp) => {
                    init[emp.id] = {};
                    pages.forEach((page) => {
                        init[emp.id][page.id] =
                            permRes.data?.[emp.id]?.[page.id] ?? false;
                    });
                });

                setPermissions(init);
                if (data.length > 0) setSelectedEmp(data[0]);
            } catch (err) {
                toast.error("Failed to load employees.");
            } finally {
                setLoading(false);
            }
        };
        fetchEmployees();
    }, []);

    const toggle = (empId, pageId) => {
        setPermissions((prev) => ({
            ...prev,
            [empId]: { ...prev[empId], [pageId]: !prev[empId][pageId] },
        }));
        setSaved(false);
    };

    const toggleAll = (empId, value) => {
        const updated = {};
        pages.forEach((p) => (updated[p.id] = value));
        setPermissions((prev) => ({ ...prev, [empId]: updated }));
        setSaved(false);
    };

    // ← UPDATED: fully wired to PUT /api/permissions/:id
    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem("token");
            await axios.put(
                `${API_BASE_URL}/api/permissions/${selectedEmp.id}`,
                { pages: permissions[selectedEmp.id] },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSaved(true);
            toast.success("Permissions saved successfully.");
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            toast.error("Failed to save permissions.");
        } finally {
            setSaving(false);
        }
    };

    const activeCount = (empId) =>
        Object.values(permissions[empId] || {}).filter(Boolean).length;

    const filtered = employees.filter((e) => {
        const fullName = `${e.firstName} ${e.lastName}`.toLowerCase();
        const dept = (e.department || "").toLowerCase();
        const desig = (e.designation || "").toLowerCase();
        const q = search.toLowerCase();
        return fullName.includes(q) || dept.includes(q) || desig.includes(q);
    });

    return (
        <div style={styles.root}>
            <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        .emp-list-scroll::-webkit-scrollbar { width: 4px; }
        .emp-list-scroll::-webkit-scrollbar-track { background: transparent; }
        .emp-list-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 99px;
        }
        .emp-list-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .emp-list-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }

        .grid-scroll::-webkit-scrollbar { width: 6px; }
        .grid-scroll::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 99px;
          margin: 8px 0;
        }
        .grid-scroll::-webkit-scrollbar-thumb {
          background: #c7d2e8;
          border-radius: 99px;
        }
        .grid-scroll::-webkit-scrollbar-thumb:hover { background: #93a8cc; }
        .grid-scroll { scrollbar-width: thin; scrollbar-color: #c7d2e8 #f8fafc; }

        .page-card:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(26,60,94,0.10) !important; }
        .emp-card-item:hover { background: #f0f7ff; }
        .emp-card-item.active:hover { background: #1a3c5e; }
      `}</style>

            <div style={styles.navbarFixed}>
                <AdminNavbar />
            </div>

            <div style={styles.body}>
                {loading ? (
                    <div style={styles.loadingWrap}>
                        <div style={styles.spinner} />
                        <p style={styles.loadingText}>Loading employees...</p>
                    </div>
                ) : (
                    <div style={styles.layout}>
                        {/* ── Sidebar ── */}
                        <div style={styles.sidebar}>
                            <div style={styles.sidebarHeader}>
                                <div style={styles.sidebarTitle}>
                                    <span style={styles.titleText}>Permission Manager</span>
                                    <span style={styles.titleSub}>Page-level access control</span>
                                </div>
                                <div style={styles.searchWrap}>
                                    <span style={styles.searchIcon}>🔍</span>
                                    <input
                                        style={styles.searchInput}
                                        placeholder="Search name, dept…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="emp-list-scroll" style={styles.empList}>
                                {filtered.length === 0 ? (
                                    <div style={styles.noEmp}>No employees found</div>
                                ) : (
                                    filtered.map((emp) => {
                                        const isActive = selectedEmp?.id === emp.id;
                                        return (
                                            <div
                                                key={emp.id}
                                                className={`emp-card-item${isActive ? " active" : ""}`}
                                                style={{
                                                    ...styles.empCard,
                                                    ...(isActive ? styles.empCardActive : {}),
                                                }}
                                                onClick={() => setSelectedEmp(emp)}
                                            >
                                                <EmpAvatar
                                                    emp={emp}
                                                    fontSize={12}
                                                    style={{
                                                        ...styles.avatar,
                                                        background: isActive
                                                            ? "rgba(255,255,255,0.2)"
                                                            : `hsl(${emp.id * 47}, 58%, 92%)`,
                                                        color: isActive
                                                            ? "#fff"
                                                            : `hsl(${emp.id * 47}, 58%, 35%)`,
                                                    }}
                                                />
                                                <div style={styles.empInfo}>
                                                    <div
                                                        style={{
                                                            ...styles.empName,
                                                            color: isActive ? "#fff" : "#1e293b",
                                                        }}
                                                    >
                                                        {emp.firstName} {emp.lastName}
                                                    </div>
                                                    <div
                                                        style={{
                                                            ...styles.empRole,
                                                            color: isActive ? "#bfdbfe" : "#94a3b8",
                                                        }}
                                                    >
                                                        {emp.designation || emp.department || emp.employeeId}
                                                    </div>
                                                </div>
                                                <div
                                                    style={{
                                                        ...styles.badge,
                                                        background: isActive
                                                            ? "rgba(255,255,255,0.15)"
                                                            : "#eff6ff",
                                                        color: isActive ? "#fff" : "#3b82f6",
                                                    }}
                                                >
                                                    {activeCount(emp.id)}/{pages.length}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* ── Permission Panel ── */}
                        <div style={styles.panel}>
                            {!selectedEmp ? (
                                <div style={styles.emptyState}>
                                    <span style={styles.emptyIcon}>👈</span>
                                    <p style={styles.emptyText}>Select an employee to manage permissions</p>
                                </div>
                            ) : (
                                <>
                                    <div style={styles.panelTop}>
                                        <div style={styles.panelHeaderRow}>
                                            <div style={styles.panelUser}>
                                                <EmpAvatar
                                                    emp={selectedEmp}
                                                    fontSize={16}
                                                    style={{
                                                        ...styles.avatarLg,
                                                        background: `hsl(${selectedEmp.id * 47}, 58%, 52%)`,
                                                        color: "#fff",
                                                    }}
                                                />
                                                <div>
                                                    <div style={styles.panelName}>
                                                        {selectedEmp.firstName} {selectedEmp.lastName}
                                                    </div>
                                                    <div style={styles.panelMeta}>
                                                        {selectedEmp.employeeId && (
                                                            <span style={styles.metaChip}>{selectedEmp.employeeId}</span>
                                                        )}
                                                        {selectedEmp.designation && (
                                                            <span style={styles.metaChip}>{selectedEmp.designation}</span>
                                                        )}
                                                        {selectedEmp.department && (
                                                            <span style={styles.metaChip}>{selectedEmp.department}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={styles.panelActions}>
                                                <button
                                                    style={styles.bulkBtn}
                                                    onClick={() => toggleAll(selectedEmp.id, true)}
                                                    disabled={saving}
                                                >
                                                    Grant All
                                                </button>
                                                <button
                                                    style={{ ...styles.bulkBtn, ...styles.bulkBtnDanger }}
                                                    onClick={() => toggleAll(selectedEmp.id, false)}
                                                    disabled={saving}
                                                >
                                                    Revoke All
                                                </button>

                                                {/* ← UPDATED: saving state + disabled guard */}
                                                <button
                                                    style={{
                                                        ...styles.saveBtn,
                                                        ...(saved ? styles.saveBtnSuccess : {}),
                                                        opacity: saving ? 0.6 : 1,
                                                        cursor: saving ? "not-allowed" : "pointer",
                                                    }}
                                                    onClick={handleSave}
                                                    disabled={saving}
                                                >
                                                    {saving ? "Saving…" : saved ? "✓ Saved!" : "Save Changes"}
                                                </button>
                                            </div>
                                        </div>

                                        <div style={styles.summaryRow}>
                                            <span style={styles.summaryText}>
                                                <b>{activeCount(selectedEmp.id)}</b> of{" "}
                                                <b>{pages.length}</b> pages accessible
                                            </span>
                                            <div style={styles.progressBar}>
                                                <div
                                                    style={{
                                                        ...styles.progressFill,
                                                        width: `${(activeCount(selectedEmp.id) / pages.length) * 100
                                                            }%`,
                                                    }}
                                                />
                                            </div>
                                            <span style={styles.pct}>
                                                {Math.round(
                                                    (activeCount(selectedEmp.id) / pages.length) * 100
                                                )}
                                                %
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid-scroll" style={styles.gridScroll}>
                                        <div style={styles.grid}>
                                            {pages.map((page) => {
                                                const allowed = permissions[selectedEmp.id]?.[page.id];
                                                return (
                                                    <div
                                                        key={page.id}
                                                        className="page-card"
                                                        style={{
                                                            ...styles.pageCard,
                                                            ...(allowed ? styles.pageCardOn : {}),
                                                        }}
                                                        onClick={() => toggle(selectedEmp.id, page.id)}
                                                    >
                                                        <span style={styles.pageIcon}>{page.icon}</span>
                                                        <span style={styles.pageLabel}>{page.label}</span>
                                                        <div
                                                            style={{
                                                                ...styles.toggle,
                                                                background: allowed ? "#22c55e" : "#cbd5e1",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    ...styles.toggleThumb,
                                                                    transform: allowed
                                                                        ? "translateX(18px)"
                                                                        : "translateX(2px)",
                                                                }}
                                                            />
                                                        </div>
                                                        <span
                                                            style={{
                                                                ...styles.statusTag,
                                                                background: allowed ? "#dcfce7" : "#f1f5f9",
                                                                color: allowed ? "#16a34a" : "#94a3b8",
                                                            }}
                                                        >
                                                            {allowed ? "Allowed" : "Denied"}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    root: {
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
    },
    navbarFixed: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        height: NAVBAR_HEIGHT,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    },
    body: {
        marginTop: NAVBAR_HEIGHT + 40,
        height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        overflow: "hidden",
        display: "flex",
        padding: "16px",
    },
    loadingWrap: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
    },
    spinner: {
        width: 40,
        height: 40,
        border: "4px solid #e2e8f0",
        borderTop: "4px solid #1a3c5e",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
    },
    loadingText: { color: "#64748b", fontSize: 14 },
    layout: {
        display: "flex",
        gap: 14,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
    },
    sidebar: {
        width: 272,
        flexShrink: 0,
        minHeight: 0,
        height: "100%",
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 2px 16px rgba(26,60,94,0.07)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    sidebarHeader: {
        padding: "16px 16px 12px",
        borderBottom: "1px solid #f1f5f9",
        flexShrink: 0,
    },
    sidebarTitle: {
        display: "flex",
        flexDirection: "column",
        marginBottom: 10,
    },
    titleText: {
        fontSize: 15,
        fontWeight: 800,
        color: "#1a3c5e",
        letterSpacing: "-0.3px",
    },
    titleSub: { fontSize: 11, color: "#94a3b8", marginTop: 2 },
    searchWrap: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 9,
        padding: "8px 10px",
    },
    searchIcon: { fontSize: 13 },
    searchInput: {
        border: "none",
        outline: "none",
        fontSize: 13,
        color: "#334155",
        background: "transparent",
        width: "100%",
    },
    empList: {
        overflowY: "auto",
        overflowX: "hidden",
        flex: 1,
        minHeight: 0,
        padding: "6px 0",
    },
    noEmp: {
        textAlign: "center",
        color: "#94a3b8",
        fontSize: 13,
        padding: "32px 16px",
    },
    empCard: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        cursor: "pointer",
        transition: "background 0.15s",
        borderLeft: "3px solid transparent",
    },
    empCardActive: {
        background: "#1a3c5e",
        borderLeftColor: "#3b82f6",
    },
    avatar: {
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
    },
    empInfo: { flex: 1, minWidth: 0 },
    empName: {
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    empRole: { fontSize: 11, marginTop: 1 },
    badge: {
        fontSize: 10,
        fontWeight: 700,
        borderRadius: 20,
        padding: "2px 7px",
        flexShrink: 0,
    },
    panel: {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 2px 16px rgba(26,60,94,0.07)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    emptyState: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        color: "#94a3b8",
    },
    emptyIcon: { fontSize: 36 },
    emptyText: { fontSize: 14 },
    panelTop: {
        padding: "20px 24px 14px",
        borderBottom: "1px solid #f1f5f9",
        flexShrink: 0,
    },
    panelHeaderRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        marginBottom: 14,
    },
    panelUser: { display: "flex", alignItems: "center", gap: 14 },
    avatarLg: {
        width: 52,
        height: 52,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        fontWeight: 800,
        color: "#fff",
        flexShrink: 0,
    },
    panelName: {
        fontSize: 17,
        fontWeight: 800,
        color: "#1a3c5e",
        marginBottom: 5,
    },
    panelMeta: { display: "flex", gap: 6, flexWrap: "wrap" },
    metaChip: {
        fontSize: 11,
        fontWeight: 600,
        background: "#f1f5f9",
        color: "#475569",
        borderRadius: 20,
        padding: "2px 10px",
    },
    panelActions: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    bulkBtn: {
        border: "1.5px solid #1a3c5e",
        background: "transparent",
        color: "#1a3c5e",
        borderRadius: 8,
        padding: "7px 14px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
    },
    bulkBtnDanger: { borderColor: "#ef4444", color: "#ef4444" },
    saveBtn: {
        background: "#1a3c5e",
        color: "#fff",
        border: "none",
        borderRadius: 9,
        padding: "8px 20px",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        transition: "background 0.3s",
    },
    saveBtnSuccess: { background: "#16a34a" },
    summaryRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    summaryText: { fontSize: 12, color: "#64748b", whiteSpace: "nowrap" },
    progressBar: {
        flex: 1,
        height: 6,
        borderRadius: 99,
        background: "#e2e8f0",
        overflow: "hidden",
    },
    progressFill: {
        height: "100%",
        background: "linear-gradient(90deg, #3b82f6, #22c55e)",
        borderRadius: 99,
        transition: "width 0.4s ease",
    },
    pct: {
        fontSize: 12,
        fontWeight: 700,
        color: "#1a3c5e",
        minWidth: 32,
        textAlign: "right",
    },
    gridScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "20px 24px 24px",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
        gap: 14,
    },
    pageCard: {
        border: "1.5px solid #e2e8f0",
        borderRadius: 12,
        padding: "16px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        cursor: "pointer",
        transition: "all 0.2s",
        background: "#fafafa",
        userSelect: "none",
    },
    pageCardOn: {
        border: "1.5px solid #3b82f6",
        background: "#eff6ff",
        boxShadow: "0 0 0 3px rgba(59,130,246,0.08)",
    },
    pageIcon: { fontSize: 22 },
    pageLabel: { fontSize: 14, fontWeight: 600, color: "#1e293b" },
    toggle: {
        width: 40,
        height: 22,
        borderRadius: 99,
        position: "relative",
        transition: "background 0.3s",
        flexShrink: 0,
    },
    toggleThumb: {
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#fff",
        position: "absolute",
        top: 2,
        transition: "transform 0.25s cubic-bezier(.4,0,.2,1)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
    },
    statusTag: {
        fontSize: 11,
        fontWeight: 700,
        borderRadius: 20,
        padding: "2px 10px",
        width: "fit-content",
        letterSpacing: "0.3px",
    },
};
